import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { map } from 'rxjs/operators';
import { BillingProvider } from '../../containers/payment/models/payment';
import { AppConfig } from '../../core/app.config';
import { BACKGROUND_NETWORK_REQ_OPTIONS } from '../../core/constants/constants';
import { ON_CALL_SUPPORT_PERSON_IMAGE_SHUFFLE_INTERVAL, SUPPORT_PERSON_IMAGES } from '../constants/support';
import { RxRequestService } from '../../core/services/rx-request.service';
import { User } from '../../core/interfaces/user';

declare const __DEPLOYMENT_ENV__: string;

export interface SupportServicePlanDetails {
  billing_provider_type: BillingProvider;
  monthly_spend: number;
  plan: string;
}

export abstract class SupportService {
  supportLink = 'mailto:support-escalations@hevodata.com';

  unreadCount$: Observable<number>;
  user: User;
  planDetails: SupportServicePlanDetails;

  isOnCallSupportAvailable$ = new BehaviorSubject(true);

  /** Emits when an in-app "Chat with Support" CTA (outside the appbar) asks to open the
   *  Cerebra widget. The widget is mounted only in the appbar, so those CTAs can't expand
   *  it directly — the appbar wrapper subscribes to this and expands the widget. */
  openChatbot$ = new Subject<void>();

  protected _liveChatIsOpen$ = new BehaviorSubject(false);

  onCallSupportPersonImage$ = timer(0, ON_CALL_SUPPORT_PERSON_IMAGE_SHUFFLE_INTERVAL).pipe(
    map(currentIndex => SUPPORT_PERSON_IMAGES[currentIndex % SUPPORT_PERSON_IMAGES.length])
  );

  constructor(protected _appConfig: AppConfig, protected _rxRequestService: RxRequestService) {
  }

  setCurrentUser(data: User) {
    this.user = data;
    this.updateSupportServiceContext(true);
    this.triggerUpdateFromBackend();
  }

  setPlanDetails(plan: SupportServicePlanDetails) {
    this.planDetails = plan;
    this.updateSupportServiceContext(false);
    this.triggerUpdateFromBackend();
  }

  abstract show(message?: string): void;

  abstract hide(): void;

  abstract resetSupportService(): void;

  abstract hideDefaultLauncher(): void;

  abstract showDefaultLauncher(): void;

  abstract updateSupportServiceContext(initial: boolean): void;

  isLiveChatOpen() {
    return this._liveChatIsOpen$.getValue();
  }

  /** Request that the Cerebra chat widget (mounted in the appbar) be opened. Callers
   *  should only invoke this when the chatbot is enabled for the deployment. */
  openChatbot() {
    this.openChatbot$.next();
  }

  reset() {
    this.user = null;
    this.planDetails = null;
    this.resetSupportService();
  }

  isChatHiddenForTeam() {
    const chatDisabledTeams = [
      {
        env: 'us',
        name: 'nextsphere.com',
        id: 53
      }
    ];

    return (
      chatDisabledTeams.findIndex(team => {
        return team.env === __DEPLOYMENT_ENV__ && team.id === this.user.teamId;
      }) >= 0
    );
  }

  triggerUpdateFromBackend() {
    this._rxRequestService
      .post(
        `${this._appConfig.getSecurityServiceURL()}/users/zendesk-and-intercom`,
        BACKGROUND_NETWORK_REQ_OPTIONS
      )
      .toPromise().then();
  }
}
